<template>
  <base-modal :is-form-visible="!!item" @close="emit('close')">
    <template #header>
      <div class="flex items-center gap-2 pl-1">
        <span v-if="!create" class="text-gray-400 tabular-nums">{{ form.id }}</span>
        <span class="text-xs px-1.5 py-0.5 rounded" :class="workItemKindClass(form.kind)">
          {{ form.kind }}
        </span>
        <span class="text-base font-semibold">
          {{ create ? 'New Work Item' : 'Edit Work Item' }}
        </span>
        <span
          v-if="item && item.is_blocked"
          class="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800"
        >blocked</span>
        <span
          v-if="item && item.is_flagged"
          class="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
        >flagged</span>
      </div>
    </template>

    <template #body>
      <form class="w-full p-3 space-y-4" @submit.prevent="save">
        <label class="block">
          <span class="text-xs uppercase tracking-wide text-gray-500">Story name</span>
          <input
            ref="titleInput"
            v-model="form.title"
            type="text"
            maxlength="255"
            class="mt-1 w-full border rounded px-2 py-1.5"
            :class="fieldErrors.title ? 'border-red-500' : ''"
          />
          <span v-if="fieldErrors.title" class="text-xs text-red-700">
            {{ fieldErrors.title }}
          </span>
        </label>

        <div class="grid grid-cols-2 gap-4">
          <label class="block">
            <!-- "Type" throughout, matching the filter on the board. It
                 used to say "Kind" and switch to "Work Item Type" once a
                 milestone was chosen -- a milestone is not a size of story,
                 it is a different sort of thing -- and one word that holds
                 for every kind of item says that without the switch. -->
            <span class="text-xs uppercase tracking-wide text-gray-500">
              Type
            </span>
            <select v-model="form.kind" class="mt-1 w-full border rounded px-2 py-1.5">
              <option v-for="k in WORK_ITEM_KINDS" :key="k.value" :value="k.value">
                {{ k.label }}
              </option>
            </select>
          </label>

          <!-- Beside the kind it belongs to, and only there: a milestone is
               the one kind that promises a date. -->
          <label v-if="isMilestone" class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">
              Target date
            </span>
            <input
              v-model="form.target_date"
              type="date"
              class="mt-1 w-full border rounded px-2 py-1.5"
            />
            <!-- Prompted but not required: a milestone can be named before
                 its date is settled. -->
            <span class="block text-xs text-gray-500 mt-0.5">
              The date this milestone is aiming at.
            </span>
          </label>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">Status</span>
            <select v-model="form.status" class="mt-1 w-full border rounded px-2 py-1.5">
              <option v-for="s in WORK_ITEM_STATUSES" :key="s.value" :value="s.value">
                {{ s.label }}
              </option>
            </select>
          </label>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">Sprint</span>
            <select v-model="form.sprint" class="mt-1 w-full border rounded px-2 py-1.5">
              <option :value="null">— no sprint —</option>
              <option v-for="s in sprints" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </label>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">Member</span>
            <select v-model="form.assignee" class="mt-1 w-full border rounded px-2 py-1.5">
              <option :value="null">— unassigned —</option>
              <option v-for="u in memberOptions" :key="u.id" :value="u.id">
                {{ u.label }}
              </option>
            </select>
          </label>

          <label class="block col-span-2">
            <span class="text-xs uppercase tracking-wide text-gray-500">
              Parent
              <span v-if="parentRequired" class="text-red-600">· required for a story</span>
              <span v-else-if="!parentKinds.length" class="text-gray-400">
                · an epic is a root, it has no parent
              </span>
            </span>
            <select
              v-model="form.parent"
              class="mt-1 w-full border rounded px-2 py-1.5 disabled:bg-gray-100"
              :class="fieldErrors.parent ? 'border-red-500' : ''"
              :disabled="!parentKinds.length"
            >
              <option :value="null">— none —</option>
              <option v-for="p in parentChoices" :key="p.id" :value="p.id">
                {{ p.id }} · {{ p.kind }} · {{ p.title }}
              </option>
            </select>
            <span v-if="fieldErrors.parent" class="text-xs text-red-700">
              {{ fieldErrors.parent }}
            </span>
          </label>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">Department</span>
            <select
              v-model="form.department"
              class="mt-1 w-full border rounded px-2 py-1.5"
              :class="fieldErrors.department ? 'border-red-500' : ''"
            >
              <!-- Only offered while creating: an existing item always has
                   one, and offering "none" would invite unsetting it. -->
              <option v-if="create" :value="null">— choose —</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
            <span v-if="fieldErrors.department" class="text-xs text-red-700">
              {{ fieldErrors.department }}
            </span>
          </label>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">Sub-department</span>
            <select v-model="form.sub_department" class="mt-1 w-full border rounded px-2 py-1.5">
              <option :value="null">— none —</option>
              <option v-for="s in subDepartmentChoices" :key="s.id" :value="s.id">
                {{ s.name }}
              </option>
            </select>
          </label>

          <label class="block">
            <span class="text-xs uppercase tracking-wide text-gray-500">Target quarter</span>
            <input
              v-model="form.target_quarter"
              type="text"
              maxlength="20"
              placeholder="e.g. 2026 Q4"
              class="mt-1 w-full border rounded px-2 py-1.5"
            />
          </label>
        </div>

        <label class="block">
          <span class="text-xs uppercase tracking-wide text-gray-500">Description</span>
          <textarea
            v-model="form.description"
            rows="8"
            class="mt-1 w-full border rounded px-2 py-1.5 font-sans"
          ></textarea>
        </label>

        <!-- The publish flag. `visibility` is a two-value enum on the model,
             but it reads as a decision here, so it is a checkbox. -->
        <div class="border rounded p-3 bg-gray-50 space-y-2">
          <label class="flex items-start gap-2">
            <input
              type="checkbox"
              class="mt-1"
              :checked="form.visibility === 'shared'"
              @change="togglePublished($event.target.checked)"
            />
            <span>
              <span class="text-sm font-medium">Publish to the Request Backlog</span>
              <span class="block text-xs text-gray-500">
                Anyone who can file a request will see the summary below —
                never this item's name or description.
              </span>
            </span>
          </label>

          <template v-if="form.visibility === 'shared'">
            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Public summary
              </span>
              <textarea
                ref="publicSummaryInput"
                v-model="form.public_summary"
                rows="3"
                class="mt-1 w-full border rounded px-2 py-1.5 font-sans"
                placeholder="What should a requester understand about this?"
              ></textarea>
              <span v-if="fieldErrors.public_summary" class="text-xs text-red-700">
                {{ fieldErrors.public_summary }}
              </span>
            </label>
            <label class="block">
              <span class="text-xs uppercase tracking-wide text-gray-500">
                Expected date (optional)
              </span>
              <input
                v-model="form.public_eta"
                type="date"
                class="mt-1 w-full border rounded px-2 py-1.5"
              />
              <span class="block text-xs text-gray-500 mt-0.5">
                A date here is a commitment a requester will read. Leave it
                empty and they see the priority band only.
              </span>
            </label>
          </template>
        </div>

        <!-- Only reachable by back-office staff, which is the same rule the
             server applies: a requester may not annotate the backlog.
             Absent while creating: an attachment needs a work item to hang
             from, and this one does not exist yet. Save it first. -->
        <div v-if="!create">
          <span class="text-xs uppercase tracking-wide text-gray-500">
            Attachments
          </span>
          <attachment-strip
            v-if="item"
            :attachments="item.attachments || []"
            target-type="work-item"
            :target-id="item.id"
            :can-upload="true"
            :can-remove="true"
            show-empty
            @changed="list => emit('attachments-changed', list)"
          />
        </div>

        <p v-if="!create" class="text-xs text-gray-400">
          Added by {{ item && item.created_by_display ? item.created_by_display : '—' }}
          <span v-if="item && item.created_at">on {{ shortDate(item.created_at) }}</span>
          <span v-if="item && item.updated_at">· last changed {{ shortDate(item.updated_at) }}</span>
        </p>

        <p v-if="error" class="text-sm text-red-700">{{ error }}</p>

        <div class="flex items-center justify-end gap-2 pt-1 pb-2">
          <!-- Archiving asks twice, in place. A native confirm() would do,
               but a modal dialog on top of a modal form blocks the page and
               reads as an error; this reads as a question. -->
          <template v-if="!create">
            <button
              v-if="!confirmingArchive"
              type="button"
              class="border border-red-300 rounded px-3 py-1.5 text-sm text-red-700
                     hover:bg-red-50 disabled:opacity-50"
              :disabled="saving || archiving"
              title="Take this off the backlog, keeping its history"
              @click="confirmingArchive = true"
            >
              Archive
            </button>
            <template v-else>
              <span class="text-xs text-red-700">Archive this item?</span>
              <button
                type="button"
                class="rounded px-3 py-1.5 text-sm text-white bg-red-600 disabled:opacity-50"
                :disabled="archiving"
                @click="archive"
              >
                {{ archiving ? 'Archiving…' : 'Yes, archive' }}
              </button>
              <button
                type="button"
                class="text-sm text-gray-600 underline"
                :disabled="archiving"
                @click="confirmingArchive = false"
              >
                Keep it
              </button>
            </template>
          </template>
          <span v-if="create" class="text-xs text-gray-400 mr-auto"></span>
          <span v-else-if="!dirty" class="text-xs text-gray-400 mr-auto">No changes yet</span>
          <span v-else class="text-xs text-gray-500 mr-auto">
            {{ changedFieldCount }} field{{ changedFieldCount === 1 ? '' : 's' }} changed
          </span>
          <button
            type="button"
            class="border rounded px-3 py-1.5 text-sm hover:bg-gray-50"
            :disabled="saving || archiving"
            @click="emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="rounded px-4 py-1.5 text-sm text-white bg-accent-primary disabled:opacity-50"
            :disabled="saving || archiving || (!create && !dirty)"
          >
            {{ saveLabel }}
          </button>
        </div>
      </form>
    </template>
  </base-modal>
</template>

<script setup>
import { computed, defineEmits, defineProps, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { archiveWorkItem, createWorkItem, updateWorkItem } from '@/services/BackOffice'
import {
  ALLOWED_PARENT_KINDS,
  KINDS_REQUIRING_PARENT,
  WORK_ITEM_KINDS,
  WORK_ITEM_STATUSES,
  workItemKindClass,
} from '@/utils/workItemOptions'
import AttachmentStrip from '@/components/back-office/AttachmentStrip.vue'

// Only these are editable here. Everything else on the serializer is either
// read-only, derived (is_blocked) or positional (rank, owned by
// drag-and-drop). visibility/public_summary/public_eta were excluded here as
// "the roadmap screen's job", but that screen was never built; the Request
// Backlog is the surface they feed, and this is where the team decides what
// to publish.
const FIELDS = [
  'title',
  'kind',
  'status',
  'sprint',
  'assignee',
  'parent',
  'department',
  'sub_department',
  'target_quarter',
  'target_date',
  'description',
  'visibility',
  'public_summary',
  'public_eta',
]

const props = defineProps({
  item: { type: Object, default: null },
  sprints: { type: Array, default: () => [] },
  users: { type: Array, default: () => [] },
  departments: { type: Array, default: () => [] },
  parentOptions: { type: Array, default: () => [] },
  // The item is a draft that does not exist on the server yet, so the form
  // posts it instead of patching it, and the parts of this screen that need
  // an id -- attachments, provenance, archiving -- stand down.
  create: { type: Boolean, default: false },
})
const emit = defineEmits([
  'close', 'saved', 'created', 'archived', 'attachments-changed',
])

const store = useStore()
const token = () => store.getters['auth/showToken']

const form = ref(blank())
const original = ref(blank())
const saving = ref(false)
const archiving = ref(false)
const confirmingArchive = ref(false)
const error = ref('')
const fieldErrors = ref({})
const titleInput = ref(null)

const publicSummaryInput = ref(null)

// Unchecking clears the public fields rather than leaving them to be
// republished by accident later; checking focuses the summary, because the
// flag on its own is not a valid state the server will accept.
function togglePublished(checked) {
  if (checked) {
    form.value.visibility = 'shared'
    nextTick(() => publicSummaryInput.value && publicSummaryInput.value.focus())
    return
  }
  form.value.visibility = 'internal'
  form.value.public_summary = ''
  form.value.public_eta = null
}

function blank() {
  const out = { id: null }
  for (const f of FIELDS) out[f] = null
  return out
}

function snapshot(item) {
  const out = { id: item.id }
  for (const f of FIELDS) {
    const value = item[f]
    // Normalise '' and undefined to null for the FKs so an untouched select
    // does not read as a change.
    out[f] = value === undefined ? null : value
  }
  out.title = item.title || ''
  out.description = item.description || ''
  out.target_quarter = item.target_quarter || ''
  out.visibility = item.visibility || 'internal'
  out.public_summary = item.public_summary || ''
  return out
}

watch(() => props.item, async item => {
  error.value = ''
  fieldErrors.value = {}
  confirmingArchive.value = false
  if (!item) return
  original.value = snapshot(item)
  form.value = { ...original.value }
  await nextTick()
  if (titleInput.value) titleInput.value.focus()
}, { immediate: true })

// A kind change can strand an illegal parent. Clear it rather than letting
// the server reject the save with a message about a field the person may not
// have touched.
watch(() => form.value.kind, (kind, previous) => {
  if (previous === undefined || kind === previous) return
  const allowed = ALLOWED_PARENT_KINDS[kind] || []
  if (!allowed.length) {
    form.value.parent = null
    return
  }
  const current = props.parentOptions.find(p => p.id === form.value.parent)
  if (current && !allowed.includes(current.kind)) form.value.parent = null
})

watch(() => form.value.department, (department, previous) => {
  if (previous === undefined || department === previous) return
  const sub = subDepartmentChoices.value.find(s => s.id === form.value.sub_department)
  if (!sub) form.value.sub_department = null
})

// The date is kept when the kind changes: a work item that stops being a
// milestone for now should not lose the date it was aiming at, and the field
// reappears with its value if it becomes one again.
const isMilestone = computed(() => form.value.kind === 'milestone')

// The list handed in is who may be given work now -- a short list, and it
// holds active accounts only. Work carries on belonging to whoever did it
// after they leave, or after they stop being offered new work. Without
// their option here the select would fall back to "unassigned" on an item
// that plainly shows an owner, and then save that, quietly taking the owner
// off the record. So the current assignee is always offered, and labelled
// for what it is: the person who has it, rather than a choice on the menu.
const memberOptions = computed(() => {
  const chosen = form.value.assignee
  if (!chosen || props.users.some(user => user.id === chosen)) return props.users
  const name = props.item?.assignee_display || `User ${chosen}`
  return [...props.users, { id: chosen, label: `${name} (current owner)` }]
})

const parentKinds = computed(() => ALLOWED_PARENT_KINDS[form.value.kind] || [])
const parentRequired = computed(() => KINDS_REQUIRING_PARENT.includes(form.value.kind))

const parentChoices = computed(() => props.parentOptions
  .filter(p => parentKinds.value.includes(p.kind) && p.id !== form.value.id)
  .sort((a, b) => a.kind.localeCompare(b.kind) || a.title.localeCompare(b.title)))

const subDepartmentChoices = computed(() => {
  const found = props.departments.find(d => d.id === form.value.department)
  return found ? (found.sub_departments || []) : []
})

const changed = computed(() => {
  const out = {}
  for (const f of FIELDS) {
    if (form.value[f] !== original.value[f]) out[f] = form.value[f]
  }
  return out
})
const changedFieldCount = computed(() => Object.keys(changed.value).length)
const dirty = computed(() => changedFieldCount.value > 0)

const saveLabel = computed(() => {
  if (props.create) return saving.value ? 'Creating…' : 'Create'
  return saving.value ? 'Saving…' : 'Save'
})

function validate() {
  const errors = {}
  if (!String(form.value.title || '').trim()) {
    errors.title = 'A work item needs a name.'
  }
  // Required by the model, and a new item is the only way to arrive here
  // without one -- so the answer comes before the round trip rather than as
  // a server error on a field nobody was looking at.
  if (!form.value.department) {
    errors.department = 'Choose a department.'
  }
  if (parentRequired.value && !form.value.parent) {
    errors.parent = `A ${form.value.kind} must hang from an epic.`
  }
  // Mirrors WorkItem.clean(): the shared surfaces show public_summary and
  // never the title, so publishing without one yields a blank row. Checked
  // here too so the answer arrives before the round trip.
  if (
    form.value.visibility === 'shared'
    && !String(form.value.public_summary || '').trim()
  ) {
    errors.public_summary = 'Write a summary before publishing.'
  }
  fieldErrors.value = errors
  return !Object.keys(errors).length
}

// Archiving is one call and no payload: what it means is the server's to
// decide, and the button should not be able to express anything else.
async function archive() {
  error.value = ''
  archiving.value = true
  try {
    const archived = await archiveWorkItem(token(), form.value.id)
    emit('archived', archived)
    emit('close')
  } catch (e) {
    error.value = describe(e)
    confirmingArchive.value = false
  } finally {
    archiving.value = false
  }
}

async function save() {
  error.value = ''
  if (!validate()) return
  // Nothing to patch is nothing to do; a new item is always worth sending,
  // because none of it has been sent before.
  if (!props.create && !dirty.value) return

  saving.value = true
  try {
    if (props.create) {
      // Every field, not just the changed ones: on a new item there is no
      // stored row for the server to fall back on for the rest.
      const payload = {}
      for (const field of FIELDS) payload[field] = form.value[field]
      emit('created', await createWorkItem(token(), payload))
      emit('close')
      return
    }
    // Patch only what changed, so two people editing different fields of the
    // same item do not overwrite each other.
    const updated = await updateWorkItem(token(), form.value.id, { ...changed.value })
    emit('saved', updated)
    emit('close')
  } catch (e) {
    const data = e?.response?.data
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const errors = {}
      for (const [field, messages] of Object.entries(data)) {
        const text = Array.isArray(messages) ? messages.join(' ') : String(messages)
        if (FIELDS.includes(field)) errors[field] = text
        else error.value = `${error.value} ${field}: ${text}`.trim()
      }
      fieldErrors.value = errors
      if (!error.value && !Object.keys(errors).length) error.value = describe(e)
    } else {
      error.value = describe(e)
    }
  } finally {
    saving.value = false
  }
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

function shortDate(value) {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}
</script>
